import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { toast } from "sonner";
import { Megaphone, X, PaperPlaneTilt, EnvelopeSimple, ChatText, ArrowLeft, UserPlus, LockSimple } from "@phosphor-icons/react";
import { moneyOrDash } from "@/lib/money";

const money = moneyOrDash;
const inputCls = "mt-1 w-full h-11 px-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500";
const RESP_STATUSES = ["new", "contacted", "hired", "passed"];

function CampaignModal({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial || {
    title: "", location: "", pay_rate: "", per_diem: "", duration: "",
    start_date: "", respond_by: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (initial?.id) {
        await api.put(`/recruitment/campaigns/${initial.id}`, f);
        onSaved(initial.id);
      } else {
        const r = await api.post("/recruitment/campaigns", f);
        onSaved(r.data.id);
      }
    } catch (e2) {
      toast.error(e2.response?.data?.detail?.toString() || e2.message);
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50" data-testid="campaign-modal">
      <form onSubmit={save} className="bg-white rounded-md w-full max-w-lg p-6 border border-slate-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">{initial?.id ? "Edit campaign" : "New recruitment campaign"}</h2>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={20} /></button>
        </div>
        <div className="mb-3">
          <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Position / title *</label>
          <input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
            data-testid="campaign-title-input" className={inputCls} placeholder="e.g. Safety Attendant — Refinery Turnaround" />
        </div>
        <div className="mb-3">
          <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Location</label>
          <input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })}
            data-testid="campaign-location-input" className={inputCls} placeholder="e.g. Ironton, OH" />
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Pay rate ($/hr)</label>
            <input type="number" step="0.01" min="0" value={f.pay_rate ?? ""} onChange={(e) => setF({ ...f, pay_rate: e.target.value })}
              data-testid="campaign-pay-input" className={inputCls} /></div>
          <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Per diem ($/day)</label>
            <input type="number" step="0.01" min="0" value={f.per_diem ?? ""} onChange={(e) => setF({ ...f, per_diem: e.target.value })}
              data-testid="campaign-perdiem-input" className={inputCls} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Expected duration</label>
            <input value={f.duration} onChange={(e) => setF({ ...f, duration: e.target.value })}
              data-testid="campaign-duration-input" className={inputCls} placeholder="e.g. 6–8 weeks" /></div>
          <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Anticipated start</label>
            <input type="date" value={f.start_date || ""} onChange={(e) => setF({ ...f, start_date: e.target.value })}
              data-testid="campaign-start-input" className={inputCls} /></div>
        </div>
        <div className="mb-3">
          <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Respond-by deadline (optional)</label>
          <input type="date" value={f.respond_by || ""} onChange={(e) => setF({ ...f, respond_by: e.target.value })}
            data-testid="campaign-respondby-input" className={inputCls} />
          <p className="text-xs text-slate-500 mt-1">Shown in the email/text for urgency. No reminders are ever sent, and responses still work after this date.</p>
        </div>
        <div className="mb-4">
          <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Notes (optional)</label>
          <textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
            data-testid="campaign-notes-input"
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            placeholder="e.g. Night and day shifts available. Steel-toe boots required." />
        </div>
        <button type="submit" disabled={busy} data-testid="campaign-save-btn"
          className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-bold rounded-md disabled:opacity-60">
          {busy ? "Saving…" : initial?.id ? "Save changes" : "Create draft campaign"}
        </button>
      </form>
    </div>
  );
}

function RecipientPicker({ campaignId, onSent }) {
  const [pool, setPool] = useState([]);
  const [picks, setPicks] = useState({}); // worker_id -> {email: bool, sms: bool}
  const [addActive, setAddActive] = useState("");
  const [added, setAdded] = useState([]); // active worker_ids added
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/recruitment/recipients").then((r) => {
      const ws = r.data.workers || [];
      setPool(ws);
      const initial = {};
      ws.filter((w) => w.status === "inactive" && !w.excluded && (w.has_email || w.has_phone))
        .forEach((w) => { initial[w.worker_id] = { email: w.has_email, sms: w.has_phone && !w.has_email }; });
      setPicks(initial);
    });
  }, []);

  const byId = useMemo(() => Object.fromEntries(pool.map((w) => [w.worker_id, w])), [pool]);
  const inactiveEligible = pool.filter((w) => w.status === "inactive" && !w.excluded && (w.has_email || w.has_phone));
  const inactiveExcluded = pool.filter((w) => w.status === "inactive" && w.excluded);
  const inactiveUnreachable = pool.filter((w) => w.status === "inactive" && !w.excluded && !w.has_email && !w.has_phone);
  const activeChoices = pool.filter((w) => w.status === "active" && !w.excluded && (w.has_email || w.has_phone) && !added.includes(w.worker_id));
  const rows = [...inactiveEligible, ...added.map((id) => byId[id]).filter(Boolean)];

  const toggle = (id, channel) => {
    setPicks((p) => {
      const cur = p[id];
      if (!cur) return { ...p, [id]: { email: channel === "email", sms: channel === "sms" } };
      const next = { ...cur, [channel]: !cur[channel] };
      if (!next.email && !next.sms) { const { [id]: _drop, ...rest } = p; return rest; }
      return { ...p, [id]: next };
    });
  };
  const toggleRow = (id) => {
    setPicks((p) => {
      if (p[id]) { const { [id]: _drop, ...rest } = p; return rest; }
      const w = byId[id];
      return { ...p, [id]: { email: w.has_email, sms: w.has_phone && !w.has_email } };
    });
  };

  const selCount = Object.keys(picks).length;

  const sendTest = async () => {
    setBusy("test");
    try {
      const r = await api.post(`/recruitment/campaigns/${campaignId}/test`);
      toast.success(`Test email sent to ${r.data.to}. (Buttons in the test won't work — it's a preview.)`);
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || e.message);
    } finally { setBusy(""); }
  };

  const send = async () => {
    if (!selCount) { toast.error("Pick at least one recipient."); return; }
    const smsCount = Object.values(picks).filter((p) => p.sms).length;
    if (!window.confirm(`Send this campaign to ${selCount} worker(s)?\n\n${Object.values(picks).filter((p) => p.email).length} email(s), ${smsCount} text message(s).\n\nThis can only be done once per campaign.`)) return;
    setBusy("send");
    try {
      const r = await api.post(`/recruitment/campaigns/${campaignId}/send`, {
        recipients: Object.entries(picks).map(([worker_id, p]) => ({ worker_id, email: !!p.email, sms: !!p.sms })),
      });
      toast.success(`Sent! ${r.data.email_sent} email(s), ${r.data.sms_sent} text(s).${r.data.skipped?.length ? ` ${r.data.skipped.length} skipped.` : ""}`);
      onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || e.message);
    } finally { setBusy(""); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 mt-4" data-testid="recipient-picker">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="tinylabel">Recipients</div>
          <p className="text-sm text-slate-500">Inactive workers are pre-selected. Untick anyone, or add an active worker below.</p>
        </div>
        <span className="text-sm font-bold text-green-700" data-testid="recipient-count">{selCount} selected</span>
      </div>

      <div className="mt-3 max-h-80 overflow-y-auto border border-slate-100 rounded-md divide-y divide-slate-100">
        {rows.length === 0 && <div className="p-4 text-sm text-slate-400" data-testid="no-recipients">No inactive workers with an email or phone on file.</div>}
        {rows.map((w) => (
          <div key={w.worker_id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`recipient-row-${w.worker_id}`}>
            <input type="checkbox" checked={!!picks[w.worker_id]} onChange={() => toggleRow(w.worker_id)}
              data-testid={`recipient-check-${w.worker_id}`} className="h-4 w-4 accent-green-600" />
            <div className="flex-1 min-w-0">
              <span className="font-medium text-slate-800">{w.name}</span>
              {w.status === "active" && <span className="ml-2 text-[10px] font-bold uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1">Active</span>}
              {w.location_hint && <span className="ml-2 text-xs text-slate-400">{w.location_hint}</span>}
            </div>
            <label className={`flex items-center gap-1 text-xs ${w.has_email ? "text-slate-600 cursor-pointer" : "text-slate-300"}`}
              title={w.has_email ? w.email : "No email on file"}>
              <input type="checkbox" disabled={!w.has_email} checked={!!picks[w.worker_id]?.email}
                onChange={() => toggle(w.worker_id, "email")} data-testid={`recipient-email-${w.worker_id}`}
                className="h-3.5 w-3.5 accent-green-600" />
              <EnvelopeSimple size={14} /> Email
            </label>
            <label className={`flex items-center gap-1 text-xs ${w.has_phone ? "text-slate-600 cursor-pointer" : "text-slate-300"}`}
              title={w.has_phone ? w.phone : "No phone on file"}>
              <input type="checkbox" disabled={!w.has_phone} checked={!!picks[w.worker_id]?.sms}
                onChange={() => toggle(w.worker_id, "sms")} data-testid={`recipient-sms-${w.worker_id}`}
                className="h-3.5 w-3.5 accent-green-600" />
              <ChatText size={14} /> Text
            </label>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <UserPlus size={16} className="text-slate-500" />
        <select value={addActive} onChange={(e) => { if (e.target.value) { setAdded((a) => [...a, e.target.value]); setPicks((p) => ({ ...p, [e.target.value]: { email: byId[e.target.value]?.has_email, sms: false } })); setAddActive(""); } }}
          data-testid="add-active-worker-select" className="h-10 px-2 border border-slate-300 rounded-md text-sm">
          <option value="">Add an active worker…</option>
          {activeChoices.map((w) => <option key={w.worker_id} value={w.worker_id}>{w.name}</option>)}
        </select>
      </div>

      {inactiveExcluded.length > 0 && (
        <div className="mt-3 text-xs text-slate-400 flex items-start gap-1.5" data-testid="excluded-note">
          <LockSimple size={13} className="mt-0.5 shrink-0" />
          <span>Excluded from future projects (never contacted): {inactiveExcluded.map((w) => w.name).join(", ")}</span>
        </div>
      )}
      {inactiveUnreachable.length > 0 && (
        <div className="mt-2 text-xs text-amber-600" data-testid="unreachable-note">
          No email or phone on file: {inactiveUnreachable.map((w) => w.name).join(", ")}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-3">
        <button onClick={sendTest} disabled={!!busy} data-testid="send-test-btn"
          className="h-11 px-4 border border-slate-300 rounded-md font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">
          {busy === "test" ? "Sending test…" : "Send test to me"}
        </button>
        <button onClick={send} disabled={!!busy || !selCount} data-testid="send-campaign-btn"
          className="h-11 px-5 bg-green-600 hover:bg-green-700 text-white font-bold rounded-md inline-flex items-center gap-2 disabled:opacity-60">
          <PaperPlaneTilt size={16} weight="bold" /> {busy === "send" ? "Sending…" : `Send to ${selCount} worker${selCount === 1 ? "" : "s"}`}
        </button>
      </div>
    </div>
  );
}

function ResponseStatusSelect({ r, onChange }) {
  return (
    <select value={r.status || "new"} onChange={(e) => onChange(r.id, e.target.value)}
      data-testid={`response-status-${r.id}`}
      className="h-8 px-2 border border-slate-300 rounded-md text-xs font-semibold">
      {RESP_STATUSES.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
    </select>
  );
}

function CampaignDetail({ cid, onBack, onEdit }) {
  const [data, setData] = useState(null);
  const load = () => api.get(`/recruitment/campaigns/${cid}`).then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [cid]);

  if (!data) return <div className="mt-6 text-slate-400">Loading…</div>;
  const c = data.campaign;
  const responses = data.responses || [];
  const interested = responses.filter((r) => r.type === "interested");
  const keepInMind = responses.filter((r) => r.type === "keep_in_mind");
  const referrals = responses.filter((r) => r.type === "referral");
  const recipients = c.recipients || [];
  const nonResponders = recipients.filter((r) => !r.responded && !r.referred);

  const setStatus = async (rid, status) => {
    try { await api.put(`/recruitment/responses/${rid}`, { status }); load(); }
    catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
  };

  const closeCamp = async () => {
    if (!window.confirm("Close this campaign? Workers who tap their link will see \"this opportunity has been filled or closed.\"")) return;
    await api.post(`/recruitment/campaigns/${cid}/close`); load();
  };
  const reopenCamp = async () => { await api.post(`/recruitment/campaigns/${cid}/reopen`); load(); };
  const delCamp = async () => {
    if (!window.confirm(`Delete campaign "${c.title}" and all its responses permanently?`)) return;
    await api.delete(`/recruitment/campaigns/${cid}`);
    onBack(true);
  };
  const convert = async (r) => {
    try {
      const resp = await api.post(`/recruitment/responses/${r.id}/convert`, {});
      toast.success("Added to Workers (as inactive — they'll flip active when assigned).");
      load();
      return resp;
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.duplicates) {
        const list = detail.duplicates.map((x) => `• ${x.name}${x.email ? ` — ${x.email}` : ""} (${x.status})`).join("\n");
        if (window.confirm(`Possible duplicate — a worker like this already exists:\n\n${list}\n\nCreate anyway?`)) {
          await api.post(`/recruitment/responses/${r.id}/convert`, { allow_duplicate: true });
          toast.success("Added to Workers.");
          load();
        }
        return;
      }
      toast.error(detail?.toString() || e.message);
    }
  };

  return (
    <div className="mt-4" data-testid="campaign-detail">
      <button onClick={() => onBack()} data-testid="campaign-back-btn"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800">
        <ArrowLeft size={15} weight="bold" /> All campaigns
      </button>

      <div className="bg-white border border-slate-200 rounded-md p-5 mt-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold" data-testid="campaign-detail-title">{c.title}</h2>
              <StatusPill status={c.status} />
            </div>
            <p className="text-sm text-slate-500 mt-1">
              {[c.location, c.pay_rate ? `${money(c.pay_rate)}/hr` : "", c.per_diem ? `${money(c.per_diem)}/day per diem` : "", c.duration]
                .filter(Boolean).join(" · ") || "No details set"}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {c.start_date ? `Starts ${c.start_date}` : ""}{c.respond_by ? ` · Respond by ${c.respond_by}` : ""}
              {c.sent_at ? ` · Sent ${String(c.sent_at).slice(0, 10)}` : " · Draft — not sent yet"}
            </p>
            {c.notes && <p className="text-sm text-slate-600 mt-2">{c.notes}</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            {c.status === "draft" && (
              <button onClick={() => onEdit(c)} data-testid="campaign-edit-btn"
                className="h-10 px-3 border border-slate-300 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-50">Edit details</button>
            )}
            {c.status === "open" && (
              <button onClick={closeCamp} data-testid="campaign-close-btn"
                className="h-10 px-3 border border-amber-300 text-amber-700 rounded-md text-sm font-semibold hover:bg-amber-50">Close campaign</button>
            )}
            {c.status === "closed" && (
              <button onClick={reopenCamp} data-testid="campaign-reopen-btn"
                className="h-10 px-3 border border-slate-300 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-50">Reopen</button>
            )}
            <button onClick={delCamp} data-testid="campaign-delete-btn"
              className="h-10 px-3 border border-rose-200 text-rose-600 rounded-md text-sm font-semibold hover:bg-rose-50">Delete</button>
          </div>
        </div>
      </div>

      {c.status === "draft" && <RecipientPicker campaignId={cid} onSent={load} />}

      {c.status !== "draft" && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            {[["Sent to", recipients.length], ["Interested", interested.length],
              ["Keep in mind", keepInMind.length], ["Referrals", referrals.length]].map(([label, n]) => (
              <div key={label} className="bg-white border border-slate-200 rounded-md p-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</div>
                <div className="text-2xl font-black mt-1" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>{n}</div>
              </div>
            ))}
          </div>

          {interested.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md p-5 mt-4" data-testid="interested-section">
              <div className="tinylabel">Interested</div>
              <div className="mt-2 divide-y divide-slate-100">
                {interested.map((r) => (
                  <div key={r.id} className="py-2.5 flex flex-wrap items-center gap-3 text-sm" data-testid={`interested-row-${r.id}`}>
                    <span className="font-semibold text-slate-800">{r.worker_name}</span>
                    {r.available_date && <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">Can start {r.available_date}</span>}
                    {r.comment && <span className="text-xs text-slate-500 italic flex-1 min-w-[140px]">"{r.comment}"</span>}
                    <span className="text-xs text-slate-400 ml-auto">{String(r.created_at).slice(0, 10)}</span>
                    <ResponseStatusSelect r={r} onChange={setStatus} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {referrals.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md p-5 mt-4" data-testid="referrals-section">
              <div className="tinylabel">Referrals</div>
              <div className="mt-2 divide-y divide-slate-100">
                {referrals.map((r) => (
                  <div key={r.id} className="py-3" data-testid={`referral-row-${r.id}`}>
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      <span className="font-semibold text-slate-800">{r.referral?.name}</span>
                      {r.referral?.email && <span className="text-slate-600">{r.referral.email}</span>}
                      {r.referral?.phone && <span className="text-slate-600">{r.referral.phone}</span>}
                      {r.duplicate_worker_id && (
                        <span className="text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
                          data-testid={`referral-dup-${r.id}`} title="This person may already be in your Workers list">
                          Possible duplicate: {r.duplicate_worker_name}
                        </span>
                      )}
                      <span className="text-xs text-slate-400 ml-auto">{String(r.created_at).slice(0, 10)}</span>
                      <ResponseStatusSelect r={r} onChange={setStatus} />
                      {r.converted_worker_id ? (
                        <span className="text-xs font-bold text-emerald-700" data-testid={`referral-converted-${r.id}`}>✓ In Workers</span>
                      ) : (
                        <button onClick={() => convert(r)} data-testid={`referral-convert-${r.id}`}
                          className="h-8 px-2.5 border border-green-300 text-green-700 rounded-md text-xs font-bold hover:bg-green-50">
                          Add to Workers
                        </button>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Recommended by <b>{r.worker_name}</b>{r.referral?.note ? ` — "${r.referral.note}"` : ""}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {keepInMind.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md p-5 mt-4" data-testid="keep-in-mind-section">
              <div className="tinylabel">Not available — keep in mind</div>
              <div className="mt-2 divide-y divide-slate-100">
                {keepInMind.map((r) => (
                  <div key={r.id} className="py-2.5 flex flex-wrap items-center gap-3 text-sm" data-testid={`kim-row-${r.id}`}>
                    <span className="font-semibold text-slate-800">{r.worker_name}</span>
                    {r.available_date && <span className="text-xs text-slate-600 bg-slate-100 rounded px-1.5 py-0.5">Available after {r.available_date}</span>}
                    <span className="text-xs text-slate-400 ml-auto">{String(r.created_at).slice(0, 10)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-md p-5 mt-4" data-testid="recipients-section">
            <div className="tinylabel">Sent to ({recipients.length})</div>
            <div className="mt-2 divide-y divide-slate-100">
              {recipients.map((r) => (
                <div key={r.worker_id} className="py-2 flex flex-wrap items-center gap-2 text-sm" data-testid={`sent-row-${r.worker_id}`}>
                  <span className="font-medium text-slate-800">{r.name}</span>
                  {r.email_sent && <span className="text-[10px] font-bold text-slate-500 border border-slate-200 rounded px-1 inline-flex items-center gap-0.5"><EnvelopeSimple size={11} /> EMAIL</span>}
                  {r.sms_sent && <span className="text-[10px] font-bold text-slate-500 border border-slate-200 rounded px-1 inline-flex items-center gap-0.5"><ChatText size={11} /> TEXT</span>}
                  {r.error && <span className="text-[10px] font-bold text-rose-600" title={r.error}>SEND FAILED</span>}
                  <span className="ml-auto text-xs">
                    {r.responded ? <span className="text-emerald-700 font-bold">Responded</span>
                      : r.referred ? <span className="text-slate-600 font-semibold">Sent referral</span>
                      : <span className="text-slate-400">No response yet</span>}
                  </span>
                </div>
              ))}
            </div>
            {nonResponders.length > 0 && (
              <p className="text-xs text-slate-400 mt-3" data-testid="non-responders-note">
                {nonResponders.length} no response yet. No automatic reminders are sent — reach out by phone if needed.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function Recruitment() {
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null); // null | {} | campaign
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/recruitment/campaigns").then((r) => setRows(Array.isArray(r.data) ? r.data : []));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (params.get("new") === "1") {
      setModal({});
      params.delete("new");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-4 md:p-8" data-testid="recruitment-page">
      <div className="tinylabel">Outreach</div>
      <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Recruitment</h1>
      <p className="text-slate-500 mt-1">Email or text workers about upcoming projects and track who's interested.</p>

      {!selected && (
        <>
          <button onClick={() => setModal({})} data-testid="create-campaign-btn"
            className="mt-5 group h-14 px-6 rounded-full bg-green-700 hover:bg-green-800 text-white font-bold text-base flex items-center gap-3 shadow-sm hover:shadow transition-all">
            <span className="p-1.5 rounded-full bg-white/20"><Megaphone size={20} weight="bold" /></span>
            Create New Campaign
            <span className="hidden sm:inline text-xs font-normal text-green-100">→ reach out about a potential project</span>
          </button>

          <div className="bg-white border border-slate-200 rounded-md overflow-x-auto mt-6">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Campaign</th>
                  <th className="text-left px-4 py-3">Location</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Sent</th>
                  <th className="text-right px-4 py-3">Interested</th>
                  <th className="text-right px-4 py-3">Referrals</th>
                  <th className="text-left px-4 py-3">Created</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-slate-400">No campaigns yet — create one to reach out about a potential project.</td></tr>}
                {rows.map((c) => (
                  <tr key={c.id} onClick={() => setSelected(c.id)} data-testid={`campaign-row-${c.id}`}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                    <td className="px-4 py-3 font-medium">{c.title}</td>
                    <td className="px-4 py-3 text-slate-600">{c.location || "—"}</td>
                    <td className="px-4 py-3"><StatusPill status={c.status} /></td>
                    <td className="px-4 py-3 text-right font-mono">{c.sent_count}</td>
                    <td className="px-4 py-3 text-right font-mono">{c.interested_count > 0 ? <span className="text-emerald-700 font-bold">{c.interested_count}</span> : 0}</td>
                    <td className="px-4 py-3 text-right font-mono">{c.referral_count}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{String(c.created_at).slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {selected && (
        <CampaignDetail cid={selected} key={selected}
          onBack={() => { setSelected(null); load(); }}
          onEdit={(c) => setModal(c)} />
      )}

      {modal && (
        <CampaignModal initial={modal.id ? modal : null}
          onClose={() => setModal(null)}
          onSaved={(id) => { setModal(null); load(); setSelected(id); }} />
      )}
    </div>
  );
}
